// src/utils/useTrades.js — the live trade/mark list, for React.
// ---------------------------------------------------------------------------
// utils/trades.js holds the tenant's catalogue as module state so tradeColor()
// and tradeLabel() can stay synchronous (they are called from PDF generation and
// the public report, where a promise has no place). Module state does not
// re-render React, so anything that DISPLAYS the list — pickers, filter chips,
// legends — subscribes through this.
//
// loadTradeCatalogue() is called once at startup. It is best-effort by design:
// a tenant with no custom types, an offline device or a failed request all leave
// the built-in trades in place, which is exactly today's behaviour.

import { useEffect, useState } from 'react';
import { getTrades, subscribeTrades, setTradeCatalogue } from './trades';

/** The live list, re-rendering when the catalogue loads or changes. */
export function useTrades() {
  const [list, setList] = useState(getTrades);
  useEffect(() => subscribeTrades(setList), []);
  return list;
}

/** Only the types still offered. Retired ones stay readable but unofferable. */
export function useActiveTrades() {
  return useTrades().filter((t) => t.active !== false);
}

let _loaded = false;

/**
 * Fetch the tenant's mark catalogue and install it. Safe to call more than once;
 * only the first call does anything.
 */
export function loadTradeCatalogue() {
  if (_loaded) return Promise.resolve(false);
  _loaded = true;
  return fetch('/api/mark-catalogue', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      if (!j || !Array.isArray(j.sets)) return false;
      // The PIN set is the one whose types are points. A tiling set of areas is
      // a measuring toolset, not a pin vocabulary, and using it to colour pins
      // would leave every pin grey.
      const pinSet = j.sets.find((s) => (s.types || []).some((t) => t.kind === 'pin'));
      return setTradeCatalogue((pinSet && pinSet.types) || []);
    })
    .catch(() => false);      // the built-in trades are a fine fallback
}
